import { api } from "../lib";

export type DeliveryStatus =
  | "pending"
  | "recalled"
  | "sending"
  | "ready_for_print"
  | "ready_for_pickup"
  | "sent"
  | "delivered"
  | "bounced"
  | "printed"
  | "mailed"
  | "failed"
  | "cancelled";

export type AggregateState =
  | "none"
  | "queued"
  | "recalled"
  | "partial_recall"
  | "sending"
  | "delivered"
  | "failed"
  | "cancelled"
  | "mixed"
  | "incomplete";

export type RecallAuthorityBasis =
  | "assigned_pathologist"
  | "any_case_capability"
  | "none";

export interface DeliveryState {
  id: string;
  recipientType: string | null;
  recipientName: string | null;
  destination: string | null;
  deliveryMethodId: string;
  deliveryMethodName: string | null;
  scheduledAt: string | null;
  status: DeliveryStatus;
  recallEligible: boolean;
}

export type DeliveryRecallOutcome =
  | "recalled"
  | "already_recalled"
  | "dispatch_started"
  | "already_sent"
  | "failed"
  | "cancelled"
  | "superseded"
  | "legacy_unbound"
  | "unavailable";

export interface DeliveryRecallResult {
  queueItemId: string;
  recipientType: string | null;
  recipientName: string | null;
  destination: string | null;
  deliveryMethodId: string;
  deliveryMethodName: string | null;
  scheduledAt: string | null;
  statusBefore: DeliveryStatus;
  statusAfter: DeliveryStatus;
  result: DeliveryRecallOutcome;
}

export interface RecallResult {
  attemptId: string;
  requestKey: string;
  serverTime: string;
  attemptedAt: string;
  recalledAt: string | null;
  caseId: string;
  signOffId: string;
  reportVersion: number;
  authorityBasis: Exclude<RecallAuthorityBasis, "none">;
  outcome: "recalled" | "partial" | "unavailable" | "already_recalled";
  recalledCount: number;
  notStoppedCount: number;
  deliveries: DeliveryRecallResult[];
}

export interface RecallAttemptSummary {
  attemptId: string;
  attemptedAt: string;
  actor: { id: string; displayName: string };
  authorityBasis: Exclude<RecallAuthorityBasis, "none"> | "denied";
  reason: string | null;
  outcome: RecallResult["outcome"] | "version_changed" | "denied" | "reverted";
  reportVersion: number | null;
  deliveries: DeliveryRecallResult[];
}

export interface RecallState {
  serverTime: string;
  caseId: string;
  signOffId: string | null;
  reportVersion: number | null;
  aggregateState: AggregateState;
  canRecall: boolean;
  authorityBasis: RecallAuthorityBasis;
  earliestPendingScheduledAt: string | null;
  recipientCount: number;
  methods: string[];
  deliveries: DeliveryState[];
  latestAttempt: RecallAttemptSummary | null;
  recallHistory: RecallAttemptSummary[];
  reconciledAttempt: RecallResult | null;
  canRevertSignoff?: boolean;
  revertSignoffReason?: string | null;
}

export interface RevertSignoffResult {
  outcome: "reverted" | "already_reverted";
  caseId: string;
  signOffId: string;
  restoredSignOffId: string | null;
  restoredReportVersion: number | null;
  requestKey: string;
}

export interface RecallRequest {
  signOffId: string;
  requestKey: string;
  reason?: string;
}

export type RecallServiceErrorKind =
  | "forbidden"
  | "version_changed"
  | "request_conflict"
  | "not_eligible"
  | "validation"
  | "unknown"
  | "failed";

export class RecallServiceError extends Error {
  readonly kind: RecallServiceErrorKind;

  constructor(kind: RecallServiceErrorKind, message: string) {
    super(message);
    this.name = "RecallServiceError";
    this.kind = kind;
    Object.setPrototypeOf(this, RecallServiceError.prototype);
  }
}

interface ApiErrorShape {
  response?: { status?: number; data?: { code?: string } };
}

function toRecallServiceError(error: unknown): RecallServiceError {
  const apiError = error as ApiErrorShape;
  const status = apiError.response?.status;
  const code = apiError.response?.data?.code;

  if (status === 403) {
    return new RecallServiceError(
      "forbidden",
      "You do not have permission to recall this report."
    );
  }
  if (status === 409 && code === "REPORT_VERSION_CHANGED") {
    return new RecallServiceError(
      "version_changed",
      "The report changed. Refresh the case before trying again."
    );
  }
  if (status === 409 && code === "REPORT_REVERT_NOT_ELIGIBLE") {
    return new RecallServiceError(
      "not_eligible",
      "This sign-off was delivered or has not been recalled. Refresh the case before trying again."
    );
  }
  if (status === 409) {
    return new RecallServiceError(
      "request_conflict",
      "This recall request could not be reconciled. Refresh the case and try again."
    );
  }
  if (status === 400 || status === 422) {
    return new RecallServiceError(
      "validation",
      "Check the recall details and try again."
    );
  }
  if (status === undefined) {
    return new RecallServiceError(
      "unknown",
      "The connection was interrupted. Check the recall outcome before trying again."
    );
  }
  return new RecallServiceError(
    "failed",
    "The report could not be recalled. Refresh the case and try again."
  );
}

export const reportDeliveryRecallService = {
  async getState(caseId: string, requestKey?: string): Promise<RecallState> {
    return api.get<RecallState>(
      `/cases/${caseId}/report-delivery-recall`,
      requestKey ? { params: { requestKey } } : undefined
    );
  },

  async recall(caseId: string, request: RecallRequest): Promise<RecallResult> {
    try {
      const reason = request.reason?.trim();
      const payload = reason
        ? { ...request, reason }
        : { signOffId: request.signOffId, requestKey: request.requestKey };
      return await api.post<RecallResult>(
        `/cases/${caseId}/report-delivery-recall`,
        payload
      );
    } catch (error) {
      throw toRecallServiceError(error);
    }
  },

  async revertSignoff(
    caseId: string,
    request: { signOffId: string; requestKey: string }
  ): Promise<RevertSignoffResult> {
    try {
      return await api.post<RevertSignoffResult>(
        `/cases/${caseId}/report-delivery-recall/revert`,
        request
      );
    } catch (error) {
      throw toRecallServiceError(error);
    }
  },
};
